import { Injectable } from '@angular/core';
import { BaseService } from '../../../../../shared/src/lib/services/base.service';
import { ISR } from '../../../../../shared/src/lib/models/index.model';
import { IGetQuery, ISearchResponse2 } from 'ets-fe-ng-sdk';
import { map } from 'rxjs';
import { IAdminAccount } from './account.admin.model';
import { IAccountSummary } from './reports.service';

@Injectable({
  providedIn: 'root',
})
export class TiedAgentAccountsService extends BaseService {
  protected override baseURL: string = 'v1/';

  getBalancePayable(companyCode: string) {
    return this.get<number>(`balance/payable`, { companyCode });
  }

  cifmAccountByCompanyCode(companyCode?: string) {
    return this.get<
      {
        amountDue: number;
        amountPaid: number;
        companyCode: string;
      }[]
    >(`company/summarize/cifm/account`).pipe(
      map((r) => (companyCode ? r?.filter((x) => x.companyCode == companyCode) : r)),
      map((r) => {
        const nr: { [companyCode: string]: (typeof r)[0] } = {};
        for (const item of r) {
          if (nr[item.companyCode]) {
            nr[item.companyCode].amountDue += item.amountDue;
            nr[item.companyCode].amountPaid += item.amountPaid;
          } else nr[item.companyCode] = item;
        }
        return Object.values(nr);
      }),
      map((r) =>
        r.map<IAccountSummary>((x) => ({
          amountDue: x.amountDue,
          amountPaid: x.amountPaid,
          company: x.companyCode,
        })),
      ),
    );
  }

  AdminAccountDetailsByCompanyCode(companyCode?: string) {
    return this.get<
      {
        amountDue: number;
        amountPaid: number;
        reason: string;
      }[]
    >(`company/summarize/accounts/details`, { company: companyCode });
  }

  CIFMaccountDetailsByCompanyCode(companyCode?: string) {
    return this.get<ICIFMAccountDetail[]>(`company/cifm/accounts/details/${companyCode}`);
  }

  createPayout = (companyCode: string, rrr?: string) => {
    return this.post<ICreatePayoutResponse>(
      null,
      `company/account/payment` + this.apiS.getRequestParse({ companyCode, rrr }),
    );
  };
  reconcilePayout = (rrr: string) => {
    return this.put<{ code: string; message: string }>({}, `company/account/payment/${rrr}`);
  };
  reconcileCIFMPayout = (rrr: string) => {
    return this.put<{ code: string; message: string }>({}, `company/cifm/account/payment/${rrr}`);
  };

  searchRRRHistory = (
    query: IGetQuery<{
      paid: boolean;
      active: boolean;
      company: string;
      rrr: string;
    }>,
  ) => this.get<ISR<IRRRHistory>>('company/account/remitta-retrieval-reference/search', query);

  searchCIFMRRRHistory = (
    query: IGetQuery<{
      companyCode: string;
      paid: boolean;
      reversed: boolean;
      rrr: string;
    }>,
  ) => this.get<ISR<ICIFMRRRHistory>>('company/account/cifm/rrr/search', query);

  cnaSearch = (
    query: IGetQuery<{
      company: string;
    }>,
  ) => this.get<ISR<IAdminAccount>>('company/account/search', query);

  cnaUpdate = (payload: IAdminAccount) => this.put<IAdminAccount>(payload, 'company/account');

  getCIINAccounts(companyCode?: string) {
    return this.get<
      {
        amountDue: number;
        amountPaid: number;
        companyCode: string;
      }[]
    >(`cifm/accounts/ciin`).pipe(
      map((r) => (companyCode ? r?.filter((x) => x.companyCode == companyCode) : r)),
    );
  }

  getCIINAccountsDetails(query?: any) {
    return this.get<ISR<ICIINAccountDetail>>(`cifm/accounts/ciin/details/search`, query).pipe(
      map((res) => res.content),
    );
  }

  createCIINAccountPayment(reference: string) {
    return this.post<{ code: string; message: string }>(
      {},
      `cifm/ciin/accounts/payment?reference=${reference}`,
    );
  }

  createCIFMPayout = (companyCode: string, rrr?: string) => {
    return this.post<ICreatePayoutResponse>(
      null,
      `company/cifm/account/payment` + this.apiS.getRequestParse({ companyCode, rrr }),
    );
  };

  companyArianSearchByCompanyCode(companyCode?: string) {
    return this.get<ISearchResponse2<IArianAccount>>(`company/arian/account/search`, { companyCode }).pipe(
      map((res) => res.content),
    );
  }

  companyArianAccountDetailsByCompanyCode(companyCode: string) {
    return this.get<ISearchResponse2<ICompanyArianAccountDetail>>(`company/arian/account/details/search`, {
      companyCode,
    }).pipe(map((res) => res.content));
  }

  searchArianRRRHistory = (
    query: IGetQuery<{
      companyCode: string;
      paid: boolean;
      reversed: boolean;
      rrr: string;
    }>,
  ) => this.get<ISR<IRRRHistory>>('company/arian/account/rrr/search', query);

  createArianPayout = (companyCode: string, rrr?: string) => {
    return this.post<ICreatePayoutResponse>(
      null,
      `company/arian/account/payment` + this.apiS.getRequestParse({ companyCode, rrr }),
    );
  };
  reconcileArianPayout = (rrr: string) => {
    return this.put<{ code: string; message: string }>({}, `company/arian/account/payment/${rrr}`);
  };
}

export interface IRRRHistory {
  processor?: EPaymentProcessor;
  id: number;
  company: string;
  rrr: string;
  amount: number;
  paid: boolean;
  active: boolean;
  paymentDue: string;
  createdOn: string;
  updatedOn: string;
  createdBy: string;
  updatedBy: string;
  reversed: boolean;
}
export interface ICIFMRRRHistory extends IRRRHistory {
  active: boolean;
  amount: number;
  companyCode: string;
  id: number;
  paid: boolean;
  paymentDue: string;
  reversed: boolean;
  rrr: string;
}

interface ICIINAccountDetail {
  candidateNo: string;
  candidateName: string;
  grossAmount: number;
  paid: boolean;
  paymentGenerated: boolean;
  companyCode: string;
  reference: string;
}

export interface ICIFMAccountDetail {
  id: number;
  candidateNo: string;
  candidateName: string;
  grossAmount: number;
  companyCode: string;
  paid: boolean;
  paymentGenerated: boolean;
  cifmFee: number;
  platformFee: number;
  rrr: string;
}

export interface IArianAccount {
  amountDue: number;
  amountPaid: number;
  companyCode: string;
}

export interface ICompanyArianAccountDetail {
  candidateCharge: number;
  candidateName: string;
  candidateNo: string;
  companyCode: string;
  externalRef: string;
  generated: boolean;
  grossAmount: number;
  paid: boolean;
  platFormCharge: number;
  reason: string;
  ria: string;
  rrr: string;
}
export enum EPaymentProcessor {
  revPay = 'SFA',
  remita = 'SSP',
}
export interface ICreatePayoutResponse {
  active: boolean;
  amount: number;
  company: string;
  id: number;
  paid: boolean;
  paymentDue: string;
  processor: EPaymentProcessor;
  reversed: boolean;
  rrr: string;
  transactionReference: string;
}
